/**
 * Yeni parola belirleme (e-postadaki baglantidan gelinir).
 *
 * Basarida TUM oturumlar kapatilir — parola sifirlamanin amaci zaten budur.
 * Kullanici yeni parolasiyla tekrar giris yapar.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiRequestError } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';
import { useToast } from '@/lib/toast';
import { useI18n } from '@/lib/i18n';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useI18n();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!token) {
    return (
      <>
        <h1 className="auth__title">{t('auth.invalidLink')}</h1>
        <div className="auth__error">
          {t('auth.invalidResetLink')}
        </div>
        <Link className="btn btn--primary btn--lg" style={{ width: '100%' }} to="/parola-sifirla">{t('auth.requestNewLink')}</Link>
      </>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setFields({});

    // Eslesme kontrolu ISTEMCIDE: sunucuya gitmeden yakalanabilecek bir hata
    // icin ag gecikmesi yasatmak gereksiz.
    if (password !== confirm) {
      setFields({ confirm: t('auth.passwordMismatch') });
      return;
    }

    setBusy(true);
    try {
      await api.post('/auth/password-reset/confirm', { token, password });
      toast.success(t('auth.passwordUpdated'));
      navigate('/giris', { replace: true });
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
        setFields(err.fields ?? {});
      } else {
        setError(t('auth.serverUnavailable'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div>
        <h1 className="auth__title">{t('auth.setNewPassword')}</h1>
        <p className="auth__sub">{t('auth.resetClosesSessions')}</p>
      </div>

      <form className="auth__form" onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="auth__error" role="alert">
            {error}
          </div>
        )}

        <Field label={t('auth.newPassword')} required error={fields['password']} hint={t('auth.passwordMin')}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
              required
              invalid={invalid}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field label={t('auth.newPasswordAgain')} required error={fields['confirm']}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              invalid={invalid}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Button type="submit" variant="primary" size="lg" loading={busy}>
          {t('auth.updatePassword')}
        </Button>
      </form>
    </>
  );
}
